import React from 'react';
import useStopCauses from '_clean-code/api/hooks/useStopCauses';
import {
  getAllComponentForAMachineOptions,
  getAllGeneralCausesForAMachineOptions,
  getAllSpecificCausesForAMachineOptions
} from '_clean-code/pages/StopsPage/stopsPage.helper';
import { StopCauseComponent } from '@clean-code/types/Stop';

// Overload 1: machineId provided -> return concrete option arrays
export function useStopOptions(machineId: number): {
  generalCauseOptions: any[];
  components: any[];
  getSpecificCauses: (generalCauseId: string | null) => any[];
  allComponents: any[];
};

// Overload 2: no machineId -> return functions that accept machineId
export function useStopOptions(): {
  generalCauseOptions: (machineId: number) => any[];
  components: (machineId: number) => any[];
  getSpecificCauses: (
    machineId: number,
    generalCauseId: string | null
  ) => any[];
  allComponents: StopCauseComponent[];
};

// Implementation
export function useStopOptions(machineId?: number) {
  const { data: stopCauses } = useStopCauses();

  // Define factory functions once
  const generalCauseOptionsByMachine = React.useCallback(
    (targetMachineId: number) =>
      getAllGeneralCausesForAMachineOptions(
        stopCauses?.treeLosses ?? [],
        targetMachineId
      ).filter((x) => x.value),
    [stopCauses?.treeLosses]
  );

  const componentsByMachine = React.useCallback(
    (targetMachineId: number) =>
      getAllComponentForAMachineOptions(
        stopCauses?.components ?? [],
        targetMachineId
      ).filter((x) => x.value),
    [stopCauses?.components]
  );

  const specificCausesByMachine = React.useCallback(
    (targetMachineId: number, generalCauseId: string | null) =>
      getAllSpecificCausesForAMachineOptions(
        stopCauses?.treeLosses ?? [],
        targetMachineId,
        generalCauseId
      ).filter((x) => x.value),
    [stopCauses?.treeLosses]
  );

  const generalCauseOptions = React.useMemo(
    () => generalCauseOptionsByMachine(machineId ?? 0),
    [generalCauseOptionsByMachine, machineId]
  );

  const components = React.useMemo(
    () => componentsByMachine(machineId ?? 0),
    [componentsByMachine, machineId]
  );

  const getSpecificCauses = React.useCallback(
    (generalCauseId: string | null) =>
      specificCausesByMachine(machineId ?? 0, generalCauseId),
    [specificCausesByMachine, machineId]
  );

  // If machineId is provided, derive values using the factory functions
  if (typeof machineId === 'number') {
    return {
      generalCauseOptions,
      components,
      getSpecificCauses,
      allComponents: stopCauses?.components ?? []
    };
  }

  // If machineId is not provided, return factory functions
  return {
    generalCauseOptions: generalCauseOptionsByMachine,
    components: componentsByMachine,
    getSpecificCauses: specificCausesByMachine,
    allComponents: stopCauses?.components ?? []
  };
}
